import { useState, useCallback } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { ToastContainer, toast } from 'react-toastify';
import * as Yup from 'yup';
import cn from 'classnames';

import { TrackerServiceInstance, UserServiceInstance } from 'services';
import { useClientContext, useUserContext, useTranslationContext } from 'hooks';

import classes from './VolunteerBlock.module.scss';
import HeaderClasses from '../Header.module.scss';
import FormClasses from 'shared/Forms/Forms.module.scss';
import ButtonClasses from 'shared/Button/Button.module.scss';

const volunteerOptions = [
  'Make calls',
  'Send texts',
  'Data entry',
  'Digital door knocking',
  'Community outreach',
  'Research',
  'Other'
];

export default function VolunteerBlock(
  props: { volunteerURL?: string | null; isOpened?: boolean; setIsOpened?: (v: boolean) => void } = {}
) {
  const { volunteerURL, isOpened, setIsOpened = (v: boolean) => { } } = props;
  const { client } = useClientContext();
  const { user } = useUserContext();
  const { t, translateCustomText } = useTranslationContext();

  const [selectedActivities, setSelectedActivities] = useState([] as string[]);

  const formik = useFormik({
    initialValues: {
      volunteerName: user.firstName || '',
      volunteerEmail: user.email || '',
      volunteerNumber: '',
      volunteerZip: user.zip || ''
    },

    validationSchema: Yup.object().shape({
      volunteerName: Yup.string().required(t("Please add your name")),
      volunteerEmail: Yup.string().required(t("Please enter your email")).email(t("Please enter valid email")),
      volunteerNumber: Yup.number().required(t("Please enter phone number")),
      volunteerZip: Yup.number().required(t("Please add zip code"))
    }),
    onSubmit: values => {
      submitVolunteer(values);
    }
  });

  const { touched, errors } = formik;

  const volunteerErrors = {
    name: errors.volunteerName && touched.volunteerName && FormClasses.hasError,
    email: errors.volunteerEmail && touched.volunteerEmail && FormClasses.hasError,
    phone: errors.volunteerNumber && touched.volunteerNumber && FormClasses.hasError,
    zip: errors.volunteerZip && touched.volunteerZip && FormClasses.hasError
  }

  const toggleVolunteerForm = useCallback(
    newValue => {
      setIsOpened(newValue);

      if (!newValue) formik.resetForm();
    },
    [formik, setIsOpened]
  );

  const toggleActivity = useCallback((activity: string) => {
    setSelectedActivities((current: string[]) =>
      current.includes(activity) ? current.filter(a => a !== activity) : [...current, activity]
    );
  }, []);

  function submitVolunteer(values: {
    volunteerName: string;
    volunteerEmail: string;
    volunteerNumber: number | string;
    volunteerZip: number | string;
  }) {
    const { volunteerName: name, volunteerEmail: email, volunteerNumber: phone, volunteerZip: zip } = values;

    if (!selectedActivities.length) {
      toast.warning(t("Please select a category"));

      return;
    }

    const error: string = (
      (errors.volunteerName && errors.volunteerName) ||
      (errors.volunteerEmail && errors.volunteerEmail) ||
      (errors.volunteerNumber && errors.volunteerNumber) ||
      (errors.volunteerZip && errors.volunteerZip) || ''
    )

    if (error) {
      toast.error(error);

      return
    }

    TrackerServiceInstance?.trackEvent('Volunteer', {
      event_category: 'Volunteers',
      userId: user.id,
      firstName: name,
      email: email,
      phone: phone + '',
      zip: zip + ''
    });

    UserServiceInstance?.submitVolunteerForm({
      selectedOptions: selectedActivities.join(', '),
      name, email,
      phone: phone.toString(),
      zip: zip.toString(),
      toEmail: client.email
    }).then(() => {
      toast.success(t("Your request has been sent!"));

      toggleVolunteerForm(!isOpened);

      formik.resetForm();
    });
  }

  function validate(event: any) {
    if (event.key.match(/[0-9\-\(\)# ]/) === null)
      event.preventDefault();
  }

  return (
    <>
      {volunteerURL ? (
        <a id="volunteer_button" className={cn(HeaderClasses.navLink, 'navlink')} target="_blank" href={volunteerURL as string}>
          {translateCustomText("headerVolunteerLinkText", t("Volunteer"))}
        </a>
      ) : (
        <>
          <span id="volunteer_button" className={cn(HeaderClasses.navLink, 'navlink')} onClick={() => toggleVolunteerForm(!isOpened)}>
            {translateCustomText("headerVolunteerLinkText", t("Volunteer"))}
          </span>

          {isOpened ? (
            <div className={cn(classes.subNav, 'subNav')}>
              <FormikProvider value={formik}>
                <Form className={cn(FormClasses.Form)}>
                  <h5>{t("How can I help virtually?")}</h5>
                  <div className={classes.formBlock}>
                    <h6>{t("Please select as many as you'd like")}</h6>
                    <div className={classes.capsuleLayout}>
                      {volunteerOptions.map(option => (
                        <button key={option} type="button"
                          className={cn(selectedActivities.includes(option) ? classes.active : null)}
                          onMouseDown={() => toggleActivity(option)}
                        >
                          {t(option)}
                        </button>
                      ))}
                    </div>

                    <div className={classes.inputWithLabel}>
                      <label htmlFor="volunteerName">{t("Name")}</label>
                      <Field type="text" name="volunteerName" className={volunteerErrors.name} placeholder={t("Enter your name")} />
                      {errors.volunteerName && touched.volunteerName && (
                        <div className={FormClasses.errorText}>{errors.volunteerName}</div>
                      )}
                    </div>

                    <div className={classes.inputWithLabel}>
                      <label htmlFor="volunteerEmail">{t("Email")}</label>
                      <Field type="email" name="volunteerEmail" className={volunteerErrors.email} placeholder={t("Enter your email")} />
                      {errors.volunteerEmail && touched.volunteerEmail && (
                        <div className={FormClasses.errorText}>{errors.volunteerEmail}</div>
                      )}
                    </div>

                    <div className={classes.inputWithLabel}>
                      <label htmlFor="volunteerNumber">{t("Phone Number")}</label>
                      <Field type="phone" name="volunteerNumber" className={volunteerErrors.phone} placeholder={t("Enter your phone number")} maxLength="16" onKeyPress={validate} />
                      {
                        errors.volunteerNumber && touched.volunteerNumber && (
                          <div className={FormClasses.errorText}>{errors.volunteerNumber}</div>
                        )
                      }
                    </div>

                    <div className={classes.inputWithLabel}>
                      <label htmlFor="volunteerZip">{t("Zip Code")}</label>
                      <Field type="tel" name="volunteerZip" className={volunteerErrors.zip} placeholder={t("Enter your zip code")} maxLength="5" onKeyPress={validate} />
                      {errors.volunteerZip && touched.volunteerZip && (
                        <div className={FormClasses.errorText}>{errors.volunteerZip}</div>
                      )}
                    </div>
                  </div>

                  <div className={classes.buttonGroup}>
                    <button type="button" className={cn(ButtonClasses.Button, classes.cancel)}
                      onClick={() => toggleVolunteerForm(!isOpened)}
                    >
                      {t("Cancel")}
                    </button>
                    <button type="submit" className={cn(ButtonClasses.Button, classes.primary)}
                      disabled={!formik.isValid}
                    > {t("I Want to Volunteer!")} </button>
                  </div>
                </Form>
              </FormikProvider>
            </div>
          ) : null}
        </>
      )}

      <ToastContainer position="top-center" />
    </>
  );
}
